import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { WalletsContext, UserAccountContextV2, MultiWalletDataContext } from "contexts";
import { ClientBFFContext } from "@finbackoffice/site-core";

export type IIMPSWithdrawForm = {
    amount: string;
    account_number: string;
    bank_code: string;
    payer_name: string;
    phone: string;
};

export const useIMPSWithdrawFormMulti = () => {
    const { currentCurrency } = useContext(WalletsContext);
    const { userData } = useContext(UserAccountContextV2);
    const { setStep, setWithdrawResponse, selectedPaymentMethod, selectedProvider } =
        useContext(MultiWalletDataContext);
    const client = useContext(ClientBFFContext);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const formMethods = useForm<IIMPSWithdrawForm>({
        mode: "onChange",
        defaultValues: {
            amount: "",
            account_number: "",
            bank_code: "",
            payer_name: "",
            phone: userData?.phone || "",
        },
    });
    const formValues = useWatch({ control: formMethods.control });

    useEffect(() => {
        setError("");
    }, [formValues]);

    const clickWithdraw = useCallback(
        async (data: IIMPSWithdrawForm) => {
            if (!selectedProvider || !currentCurrency || !selectedPaymentMethod) return;
            try {
                setLoading(true);
                const response = await client.createWithdrawal({
                    provider: selectedProvider,
                    currency: currentCurrency,
                    amount: data.amount,
                    provider_data: {
                        payment_method: selectedPaymentMethod,
                        account_number: data.account_number.replaceAll(" ", ""),
                        bank_code: data.bank_code,
                        payer_name: data.payer_name.replaceAll(" ", ""),
                        phone: data.phone,
                    },
                });

                if (response) {
                    setWithdrawResponse(response);
                    setStep("status");
                }
            } catch (err: any) {
                const error = err.response.data ?? {};

                if (error.error) {
                    const details = error.details;
                    switch (error.error) {
                        case "payments_fulfillment_exception": {
                            let detailedMessage = "";

                            if (details) {
                                if (details.max_withdrawal_error) {
                                    detailedMessage = details.max_withdrawal_error;
                                } else {
                                    for (const key in details) {
                                        detailedMessage += details[key] + "\n";
                                    }
                                }
                            }

                            if (detailedMessage) {
                                setError(detailedMessage);
                            } else {
                                setError(error.message);
                            }
                            break;
                        }
                        case "validation_error": {
                            if (details) {
                                if (details.provider_data) {
                                    for (const field in details.provider_data) {
                                        let detailedMessage = "";
                                        for (const key in details.provider_data[field]) {
                                            detailedMessage +=
                                                details.provider_data[field][key] + "\n";
                                        }
                                        formMethods.setError(field as keyof IIMPSWithdrawForm, {
                                            message: detailedMessage,
                                        });
                                    }
                                }
                            } else {
                                setError(error.message);
                            }
                            break;
                        }
                        default:
                            setError(error.message);
                    }
                }

                setLoading(false);
            }
        },
        [
            selectedProvider,
            currentCurrency,
            selectedPaymentMethod,
            client,
            setWithdrawResponse,
            setStep,
            formMethods,
        ],
    );

    return useMemo(
        () => ({
            clickWithdraw,
            error,
            loading,
            formMethods,
        }),
        [clickWithdraw, error, formMethods, loading],
    );
};
